import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useEntity } from '../../context/EntityContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import {
  DollarSign,
  Check,
  X,
  Clock,
  CheckCircle2,
  AlertCircle,
  Edit3,
  Eye,
  FileText,
  Download,
  Send,
  User,
  Building,
  Percent,
  Calculator,
  Users,
  ArrowRight,
  History,
  Shield,
} from 'lucide-react';

const statusColors = {
  draft: 'bg-gray-500',
  submitted: 'bg-blue-500',
  under_review: 'bg-yellow-500',
  revision_requested: 'bg-orange-500',
  pic_approved: 'bg-cyan-500',
  admin_approved: 'bg-indigo-500',
  approved: 'bg-green-500',
  paid: 'bg-emerald-600',
  rejected: 'bg-red-500',
};

const statusLabels = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  revision_requested: 'Revision Requested',
  pic_approved: 'PIC Approved',
  admin_approved: 'Admin Approved',
  approved: 'Fully Approved',
  paid: 'Paid',
  rejected: 'Rejected',
};

const AdminCommissionClaims = () => {
  const { user } = useAuth();
  const { currentEntity } = useEntity();
  const [searchParams] = useSearchParams();
  const agentIdFilter = searchParams.get('agent_id');
  const [agentInfo, setAgentInfo] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionComment, setRevisionComment] = useState('');
  const [fieldComments, setFieldComments] = useState({});
  const [auditTrailOpen, setAuditTrailOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);

  // Role-based permissions
  const isPIC = user?.role === 'pic';
  const isAccount = user?.role === 'account';
  const isAdmin = user?.role === 'admin';
  const canApprove = isPIC || isAdmin || isAccount;
  // Account can process payment after full approval
  const canProcessPayment = isAccount;
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    loadClaims();
    loadDashboardStats();
    if (agentIdFilter) {
      loadAgentInfo();
    }
  }, [agentIdFilter, currentEntity]);

  const loadAgentInfo = async () => {
    try {
      const res = await api.get(`/users/${agentIdFilter}`);
      setAgentInfo(res.data);
    } catch (error) {
      console.error('Error loading agent info:', error);
    }
  };

  const loadClaims = async () => {
    try {
      let url = '/commission-claims';
      if (agentIdFilter) {
        url = `/agents/${agentIdFilter}/commission-claims`;
      }
      const entityParam = currentEntity !== 'all' ? `?entity=${currentEntity}` : '';
      const res = await api.get(`${url}${entityParam}`);
      setClaims(res.data);
    } catch (error) {
      console.error('Error loading claims:', error);
      toast.error('Failed to load claims');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const entityParam = currentEntity !== 'all' ? `?entity=${currentEntity}` : '';
      const res = await api.get(`/commission-claims/dashboard/stats${entityParam}`);
      setDashboardStats(res.data);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const loadClaimDetails = async (claimId) => {
    try {
      const res = await api.get(`/commission-claims/${claimId}`);
      setSelectedClaim(res.data);
      setDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to load claim details');
    }
  };

  const loadAuditTrail = async (claimId) => {
    try {
      const res = await api.get(`/commission-claims/${claimId}/audit-trail`);
      setAuditData(res.data);
      setAuditTrailOpen(true);
    } catch (error) {
      toast.error('Failed to load audit trail');
    }
  };

  const handleStartReview = async (claimId) => {
    if (!canApprove) {
      toast.error('Only PIC or Account roles can start review');
      return;
    }
    try {
      await api.post(`/commission-claims/${claimId}/start-review`);
      toast.success('Review started');
      loadClaims();
      if (selectedClaim?.id === claimId) {
        loadClaimDetails(claimId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start review');
    }
  };

  const handleApprove = async (claimId) => {
    if (!canApprove) {
      toast.error('Only PIC, Admin, or Account roles can approve claims');
      return;
    }
    try {
      const res = await api.post(`/commission-claims/${claimId}/approve`);
      toast.success(res.data?.message || 'Claim approved');
      loadClaims();
      if (selectedClaim?.id === claimId) {
        loadClaimDetails(claimId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve claim');
    }
  };

  const handleProcessPayment = async (claimId) => {
    if (!canProcessPayment) {
      toast.error('Only Admin can process payments');
      return;
    }
    try {
      const res = await api.post(`/commission-claims/${claimId}/process-payment`);
      toast.success(`Payment processed! ${res.data.payments} payments totaling ${formatCurrency(res.data.total_paid)}`);
      loadClaims();
      if (selectedClaim?.id === claimId) {
        loadClaimDetails(claimId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    }
  };

  const handleRequestRevision = async () => {
    if (!selectedClaim) return;
    if (!canApprove) {
      toast.error('Only PIC, Admin, or Account roles can request revisions');
      return;
    }
    try {
      await api.post(`/commission-claims/${selectedClaim.id}/request-revision`, {
        field_comments: fieldComments,
        general_comment: revisionComment,
      });
      toast.success('Revision requested');
      setRevisionDialogOpen(false);
      setRevisionComment('');
      setFieldComments({});
      loadClaims();
      loadClaimDetails(selectedClaim.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request revision');
    }
  };

  const handleReject = async (claimId, reason) => {
    if (!canApprove) {
      toast.error('Only PIC, Admin, or Account roles can reject claims');
      return;
    }
    try {
      await api.post(`/commission-claims/${claimId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Claim rejected');
      loadClaims();
      setDetailsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject claim');
    }
  };

  const filteredClaims = claims.filter((c) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return ['submitted', 'under_review'].includes(c.status);
    return c.status === activeTab;
  });

  const pendingCount = claims.filter((c) => ['submitted', 'under_review'].includes(c.status)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-commission-claims">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-blue-600" />
            {agentIdFilter && agentInfo ? `Claims - ${agentInfo.full_name}` : 'Claims Management'}
          </h1>
          <p className="text-gray-500">
            {agentIdFilter && agentInfo 
              ? `Viewing commission claims for ${agentInfo.agent_code || agentInfo.full_name}` 
              : 'Review and process commission claims'}
          </p>
        </div>
        {pendingCount > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {pendingCount} Pending Review
          </Badge>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Claims</p>
                <p className="text-xl font-bold">{dashboardStats?.total_claims || claims.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={dashboardStats?.pending_for_you > 0 ? 'border-yellow-300 bg-yellow-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  {isPIC ? 'Awaiting Your Review' : isAccount ? 'Awaiting Your Action' : 'Pending'}
                </p>
                <p className="text-xl font-bold text-yellow-600">{dashboardStats?.pending_for_you || pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">PIC Approved</p>
                <p className="text-xl font-bold">{dashboardStats?.by_status?.pic_approved?.count || claims.filter((c) => c.status === 'pic_approved').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Fully Approved</p>
                <p className="text-xl font-bold">{dashboardStats?.by_status?.approved?.count || claims.filter((c) => c.status === 'approved').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(dashboardStats?.total_paid_amount || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Claims Table */}
      <Card>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b px-4">
              <TabsList className="h-12 bg-transparent">
                <TabsTrigger value="pending" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600">
                  Pending {pendingCount > 0 && `(${pendingCount})`}
                </TabsTrigger>
                <TabsTrigger value="pic_approved">PIC Approved</TabsTrigger>
                <TabsTrigger value="admin_approved">Admin Approved</TabsTrigger>
                <TabsTrigger value="approved">Fully Approved</TabsTrigger>
                <TabsTrigger value="paid">Paid</TabsTrigger>
                <TabsTrigger value="revision_requested">Revision</TabsTrigger>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>SPA Price</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Co-Broke</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClaims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                      No claims found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClaims.map((claim) => (
                    <TableRow key={claim.id} data-testid={`claim-row-${claim.id}`}>
                      <TableCell className="font-mono text-sm">{claim.claim_ref}</TableCell>
                      <TableCell>
                        <Badge className={claim.entity === 'sp_sam' ? 'bg-blue-500 text-white' : 'bg-emerald-500 text-white'}>
                          {claim.entity === 'sp_sam' ? 'SP SAM' : 'SAM Rumah'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{claim.agent_name}</p>
                          <p className="text-xs text-gray-500">{claim.agent_code}</p>
                        </div>
                      </TableCell>
                      <TableCell>{claim.project_name}</TableCell>
                      <TableCell>{claim.unit_number}</TableCell>
                      <TableCell>{formatCurrency(claim.spa_price)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{formatCurrency(claim.total_commission)}</p>
                          <p className="text-xs text-gray-500">{claim.commission_percentage}%</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {claim.is_cobroke ? (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">
                            Yes
                          </Badge>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[claim.status]} text-white`}>
                          {statusLabels[claim.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDate(claim.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => loadClaimDetails(claim.id)}
                            data-testid={`view-claim-${claim.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {/* PIC can start review on submitted claims */}
                          {claim.status === 'submitted' && isPIC && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStartReview(claim.id)}
                            >
                              Start Review
                            </Button>
                          )}
                          {/* PIC can approve submitted/under_review → pic_approved */}
                          {['submitted', 'under_review'].includes(claim.status) && isPIC && (
                            <Button
                              size="sm"
                              className="bg-cyan-600 hover:bg-cyan-700"
                              onClick={() => handleApprove(claim.id)}
                            >
                              <Check className="h-4 w-4 mr-1" /> PIC Approve
                            </Button>
                          )}
                          {/* Admin can approve pic_approved → admin_approved */}
                          {claim.status === 'pic_approved' && isAdmin && (
                            <Button
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700"
                              onClick={() => handleApprove(claim.id)}
                            >
                              <Check className="h-4 w-4 mr-1" /> Admin Approve
                            </Button>
                          )}
                          {/* Account can approve admin_approved → approved */}
                          {claim.status === 'admin_approved' && isAccount && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApprove(claim.id)}
                            >
                              <Check className="h-4 w-4 mr-1" /> Account Approve
                            </Button>
                          )}
                          {/* Account can process payment after full approval */}
                          {claim.status === 'approved' && isAccount && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleProcessPayment(claim.id)}
                            >
                              <DollarSign className="h-4 w-4 mr-1" /> Pay
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Tabs>
        </CardContent>
      </Card>

      {/* Claim Details Sheet */}
      <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
          {selectedClaim && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Claim {selectedClaim.claim_ref}
                </SheetTitle>
                <SheetDescription>
                  Submitted by {selectedClaim.agent_name} on {formatDate(selectedClaim.created_at)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <Badge className={`${statusColors[selectedClaim.status]} text-white px-3 py-1`}>
                    {statusLabels[selectedClaim.status]}
                  </Badge>
                  {selectedClaim.revision_count > 0 && (
                    <Badge variant="outline" className="text-orange-600">
                      {selectedClaim.revision_count} Revision(s)
                    </Badge>
                  )}
                </div>

                {/* Customer Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="h-4 w-4" /> Customer Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Full Name</p>
                      <p className="font-medium">{selectedClaim.customer_full_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">NRIC</p>
                      <p className="font-medium">{selectedClaim.customer_nric}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Contact</p>
                      <p className="font-medium">{selectedClaim.customer_contact_no}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Email</p>
                      <p className="font-medium">{selectedClaim.customer_email}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Transaction Details */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building className="h-4 w-4" /> Transaction Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Project</p>
                      <p className="font-medium">{selectedClaim.project_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Unit</p>
                      <p className="font-medium">{selectedClaim.unit_number}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">SPA Price</p>
                      <p className="font-medium">{formatCurrency(selectedClaim.spa_price)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Nett Price</p>
                      <p className="font-medium">{formatCurrency(selectedClaim.nett_price)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Booking Date</p>
                      <p className="font-medium">{selectedClaim.booking_date}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Signing Date</p>
                      <p className="font-medium">{selectedClaim.signing_date}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Bank</p>
                      <p className="font-medium">{selectedClaim.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Loan Amount</p>
                      <p className="font-medium">{formatCurrency(selectedClaim.loan_amount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Commission %</p>
                      <p className="font-medium">{selectedClaim.commission_percentage}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Lot Status</p>
                      <p className="font-medium capitalize">{selectedClaim.lot_status}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Management Recipients */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Users className="h-4 w-4" /> Management Commission Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rank</TableHead>
                          <TableHead>Agent</TableHead>
                          <TableHead className="text-right">%</TableHead>
                          <TableHead className="text-right">Gross</TableHead>
                          <TableHead className="text-right">SST (8%)</TableHead>
                          <TableHead className="text-right">Platform (5%)</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(selectedClaim.management_recipients || []).map((r, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{r.rank_display}</TableCell>
                            <TableCell>
                              <div>
                                <p>{r.name}</p>
                                <p className="text-xs text-gray-500">{r.agent_code}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{r.rate_percentage || '-'}%</TableCell>
                            <TableCell className="text-right">{formatCurrency(r.gross_comm)}</TableCell>
                            <TableCell className="text-right text-red-600">-{formatCurrency(r.sst_amount)}</TableCell>
                            <TableCell className="text-right text-orange-600">-{formatCurrency(r.platform_fee)}</TableCell>
                            <TableCell className="text-right font-semibold text-green-600">{formatCurrency(r.balance_comm)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-gray-50">
                          <TableCell colSpan={6} className="font-semibold">Total Management Commission</TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(selectedClaim.total_management_comm)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Co-broke Recipients */}
                {selectedClaim.is_cobroke && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" /> Co-Broke Commission Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Level</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead className="text-right">%</TableHead>
                            <TableHead className="text-right">Gross</TableHead>
                            <TableHead className="text-right">SST (8%)</TableHead>
                            <TableHead className="text-right">Balance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(selectedClaim.cobroke_recipients || []).map((r, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{r.rank_display || r.level_display}</TableCell>
                              <TableCell>{r.name}</TableCell>
                              <TableCell className="text-right">{r.rate_percentage || '-'}%</TableCell>
                              <TableCell className="text-right">{formatCurrency(r.gross_comm)}</TableCell>
                              <TableCell className="text-right text-red-600">-{formatCurrency(r.sst_amount)}</TableCell>
                              <TableCell className="text-right font-semibold text-purple-600">{formatCurrency(r.balance_comm)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-purple-50">
                            <TableCell colSpan={5} className="font-semibold">Total Co-Broke Commission</TableCell>
                            <TableCell className="text-right font-bold text-purple-600">
                              {formatCurrency(selectedClaim.total_cobroke_comm)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Total */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calculator className="h-5 w-5 text-blue-600" />
                        <span className="font-semibold">Total Commission</span>
                      </div>
                      <span className="text-2xl font-bold text-blue-600">
                        {formatCurrency(selectedClaim.total_commission)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Documents */}
                {selectedClaim.documents && Object.keys(selectedClaim.documents).length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4" /> Supporting Documents
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(selectedClaim.documents).map(([type, docs]) => (
                          <div key={type} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span className="text-sm capitalize">{type.replace(/_/g, ' ')}</span>
                            <Badge variant="outline">{Array.isArray(docs) ? docs.length : 1}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Revision History */}
                {selectedClaim.revision_requests && selectedClaim.revision_requests.length > 0 && (
                  <Card className="border-orange-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-orange-600">
                        <AlertCircle className="h-4 w-4" /> Revision History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedClaim.revision_requests.map((rev, idx) => (
                          <div key={idx} className="p-3 bg-orange-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">{rev.requested_by_name}</span>
                              <span className="text-xs text-gray-500">{formatDate(rev.requested_at)}</span>
                            </div>
                            {rev.general_comment && (
                              <p className="text-sm text-gray-700">{rev.general_comment}</p>
                            )}
                            {rev.field_comments && Object.keys(rev.field_comments).length > 0 && (
                              <div className="mt-2 space-y-1">
                                {Object.entries(rev.field_comments).map(([field, comment]) => (
                                  <div key={field} className="text-xs">
                                    <span className="font-medium capitalize">{field.replace(/_/g, ' ')}:</span>{' '}
                                    <span className="text-gray-600">{comment}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4 border-t flex-wrap">
                  {/* Audit Trail Button - Always available */}
                  <Button
                    variant="outline"
                    onClick={() => loadAuditTrail(selectedClaim.id)}
                  >
                    <History className="h-4 w-4 mr-1" /> Audit Trail
                  </Button>
                  
                  {/* PIC can start review on submitted claims */}
                  {selectedClaim.status === 'submitted' && isPIC && (
                    <Button onClick={() => handleStartReview(selectedClaim.id)}>
                      Start Review
                    </Button>
                  )}
                  
                  {/* PIC can approve submitted/under_review claims → becomes pic_approved */}
                  {['submitted', 'under_review'].includes(selectedClaim.status) && isPIC && (
                    <>
                      <Button
                        className="bg-cyan-600 hover:bg-cyan-700"
                        onClick={() => handleApprove(selectedClaim.id)}
                      >
                        <Check className="h-4 w-4 mr-1" /> PIC Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="text-orange-600 border-orange-300"
                        onClick={() => setRevisionDialogOpen(true)}
                      >
                        <Edit3 className="h-4 w-4 mr-1" /> Request Revision
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-300"
                        onClick={() => {
                          const reason = prompt('Enter rejection reason:');
                          if (reason) handleReject(selectedClaim.id, reason);
                        }}
                      >
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  
                  {/* Admin can approve pic_approved claims → becomes admin_approved */}
                  {selectedClaim.status === 'pic_approved' && isAdmin && (
                    <>
                      <Button
                        className="bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => handleApprove(selectedClaim.id)}
                      >
                        <Check className="h-4 w-4 mr-1" /> Admin Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="text-orange-600 border-orange-300"
                        onClick={() => setRevisionDialogOpen(true)}
                      >
                        <Edit3 className="h-4 w-4 mr-1" /> Request Revision
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-300"
                        onClick={() => {
                          const reason = prompt('Enter rejection reason:');
                          if (reason) handleReject(selectedClaim.id, reason);
                        }}
                      >
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  
                  {/* Account can approve admin_approved claims → becomes approved */}
                  {selectedClaim.status === 'admin_approved' && isAccount && (
                    <>
                      <Button
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApprove(selectedClaim.id)}
                      >
                        <Check className="h-4 w-4 mr-1" /> Account Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="text-orange-600 border-orange-300"
                        onClick={() => setRevisionDialogOpen(true)}
                      >
                        <Edit3 className="h-4 w-4 mr-1" /> Request Revision
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-300"
                        onClick={() => {
                          const reason = prompt('Enter rejection reason:');
                          if (reason) handleReject(selectedClaim.id, reason);
                        }}
                      >
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  
                  {/* PIC Approved - waiting for Admin */}
                  {selectedClaim.status === 'pic_approved' && isPIC && (
                    <div className="flex items-center gap-2 text-cyan-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm">PIC Approved - Awaiting Admin approval</span>
                    </div>
                  )}
                  
                  {/* Admin Approved - waiting for Account */}
                  {selectedClaim.status === 'admin_approved' && (isPIC || isAdmin) && (
                    <div className="flex items-center gap-2 text-indigo-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm">Admin Approved - Awaiting Account final approval</span>
                    </div>
                  )}
                  
                  {/* Account can process payment after full approval */}
                  {selectedClaim.status === 'approved' && isAccount && (
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleProcessPayment(selectedClaim.id)}
                    >
                      <DollarSign className="h-4 w-4 mr-1" /> Process Payment
                    </Button>
                  )}
                  
                  {/* Fully approved - PIC sees waiting for Account payment */}
                  {selectedClaim.status === 'approved' && isPIC && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm">Fully Approved - Awaiting Account payment processing</span>
                    </div>
                  )}
                  
                  {selectedClaim.status === 'paid' && (
                    <div className="flex items-center gap-2 text-emerald-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Payment completed on {formatDate(selectedClaim.paid_at)}</span>
                    </div>
                  )}
                  
                  {/* Admin view-only message */}
                  {isAdmin && (
                    <div className="flex items-center gap-2 text-gray-500 ml-auto">
                      <Eye className="h-4 w-4" />
                      <span className="text-sm">Admin view only</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Revision Request Dialog */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Revision</DialogTitle>
            <DialogDescription>
              Specify what needs to be corrected. The agent will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>General Comment</Label>
              <Textarea
                placeholder="Describe what needs to be revised..."
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Field-Specific Comments (Optional)</Label>
              <div className="grid grid-cols-2 gap-2">
                {['customer_full_name', 'customer_nric', 'spa_price', 'commission_percentage', 'documents'].map((field) => (
                  <Input
                    key={field}
                    placeholder={field.replace(/_/g, ' ')}
                    value={fieldComments[field] || ''}
                    onChange={(e) => setFieldComments({ ...fieldComments, [field]: e.target.value })}
                    className="text-sm"
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevisionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRequestRevision} className="bg-orange-600 hover:bg-orange-700">
              <Send className="h-4 w-4 mr-1" /> Send Revision Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog open={auditTrailOpen} onOpenChange={setAuditTrailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Audit Trail - {auditData?.claim_ref}
            </DialogTitle>
            <DialogDescription>
              Complete history of all actions taken on this claim
            </DialogDescription>
          </DialogHeader>
          
          {auditData && (
            <div className="space-y-6 py-4">
              {/* Audit Trail Entries */}
              {auditData.audit_trail && auditData.audit_trail.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-blue-600" />
                    Action Log
                  </h4>
                  <div className="space-y-3">
                    {auditData.audit_trail.map((entry, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={
                            entry.action === 'approved' ? 'bg-green-500' :
                            entry.action === 'rejected' ? 'bg-red-500' :
                            entry.action === 'revision_requested' ? 'bg-orange-500' :
                            'bg-blue-500'
                          }>
                            {entry.action.replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                          <span className="text-xs text-gray-500">{formatDate(entry.timestamp)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">{entry.performed_by_name}</span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {entry.performed_by_role?.toUpperCase()}
                          </Badge>
                        </div>
                        {entry.notes && (
                          <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>
                        )}
                        {entry.reason && (
                          <p className="text-sm text-red-600 mt-1">Reason: {entry.reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status History */}
              {auditData.status_history && auditData.status_history.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-600" />
                    Status History
                  </h4>
                  <div className="space-y-2">
                    {auditData.status_history.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <Badge className={statusColors[entry.status]}>
                            {statusLabels[entry.status]}
                          </Badge>
                          {entry.changed_by_role && (
                            <span className="text-xs text-gray-500">
                              by {entry.changed_by_role.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(entry.changed_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Revision Requests */}
              {auditData.revision_requests && auditData.revision_requests.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Edit3 className="h-4 w-4 text-orange-600" />
                    Revision Requests
                  </h4>
                  <div className="space-y-3">
                    {auditData.revision_requests.map((req, idx) => (
                      <div key={idx} className="p-3 bg-orange-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-medium">{req.requested_by_name}</span>
                            {req.requested_by_role && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                {req.requested_by_role.toUpperCase()}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(req.requested_at)}</span>
                        </div>
                        {req.general_comment && (
                          <p className="text-sm text-gray-700">{req.general_comment}</p>
                        )}
                        {req.field_comments && Object.keys(req.field_comments).length > 0 && (
                          <div className="mt-2 text-xs space-y-1">
                            {Object.entries(req.field_comments).map(([field, comment]) => (
                              <div key={field}>
                                <span className="font-medium capitalize">{field.replace(/_/g, ' ')}:</span>{' '}
                                <span className="text-gray-600">{comment}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!auditData.audit_trail || auditData.audit_trail.length === 0) &&
               (!auditData.status_history || auditData.status_history.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No audit trail available yet</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditTrailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommissionClaims;
